/**
 * Waiting Analytics Service
 * API calls for waiting screen analytics
 */

import { apiConfig } from '../config/api';

const BASE_URL = apiConfig.brandsApi;

export interface SessionStats {
  totalSessions: number;
  completedSessions: number;
  completionRate: number;
  avgWaitingTime: number;
  avgGeneratingTime: number;
}

export interface EngagementStats {
  continueClickRate: number;
  reelsSwipeCount: number;
  avgReelsViewed: number;
  productClickCount: number;
  avgVideoWatchTime: number;
  bounceRate: number;
}

export interface EventCounts {
  waiting_started: number;
  waiting_ready: number;
  continue_clicked: number;
  waiting_completed: number;
  reels_swipe: number;
  reels_view: number;
  product_click: number;
  chat_opened: number;
}

export interface WaitingAnalytics {
  sessions: SessionStats;
  engagement: EngagementStats;
  eventCounts: EventCounts;
  periodStart?: string;
  periodEnd?: string;
}

export interface WaitingType {
  value: string;
  label: string;
}

export const WAITING_TYPES: WaitingType[] = [
  { value: 'all', label: 'Tümü' },
  { value: 'fashplay', label: 'FashPlay' },
  { value: 'fashsnap', label: 'FashSnap' },
  { value: 'fashmatch', label: 'FashMatch' },
];

export class WaitingAnalyticsService {
  /**
   * Get waiting screen analytics for a brand
   */
  static async getAnalytics(
    brandId: string,
    waitingType?: string,
    startDate?: string,
    endDate?: string
  ): Promise<WaitingAnalytics> {
    try {
      const params = new URLSearchParams();
      if (waitingType && waitingType !== 'all') {
        params.append('waitingType', waitingType);
      }
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const queryString = params.toString();
      const url = `${BASE_URL}/api/v1/waiting/analytics/${brandId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch waiting analytics');
      }

      if (!data.success || !data.data) {
        throw new Error('Invalid API response');
      }

      return data.data;
    } catch (error) {
      console.error('Failed to fetch waiting analytics:', error);
      
      return {
        sessions: {
          totalSessions: 0,
          completedSessions: 0,
          completionRate: 0,
          avgWaitingTime: 0,
          avgGeneratingTime: 0
        },
        engagement: {
          continueClickRate: 0,
          reelsSwipeCount: 0,
          avgReelsViewed: 0,
          productClickCount: 0,
          avgVideoWatchTime: 0,
          bounceRate: 0
        },
        eventCounts: {
          waiting_started: 0,
          waiting_ready: 0,
          continue_clicked: 0,
          waiting_completed: 0,
          reels_swipe: 0,
          reels_view: 0,
          product_click: 0,
          chat_opened: 0
        }
      };
    }
  }

  /**
   * Download weekly report as PDF
   */
  static async downloadWeeklyReport(
    brandId: string,
    waitingType?: string
  ): Promise<void> {
    try {
      const params = new URLSearchParams();
      if (waitingType && waitingType !== 'all') {
        params.append('waitingType', waitingType);
      }

      const queryString = params.toString();
      const url = `${BASE_URL}/api/v1/waiting/report/${brandId}/weekly${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'analytics-report.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to download weekly report:', error);
      throw error;
    }
  }
}
